import { useState } from "react";
import { Loader2, Check, X, ShieldCheck, Trash2, Ban, Power, UserPlus, Link2, KeyRound, Copy } from "lucide-react";
import { toast } from "sonner";
import { TopBar } from "@/components/layout/TopBar";
import { TeamsSection } from "@/components/auth/TeamsSection";
import {
  useAccessRequests,
  useApproveRequest,
  useDenyRequest,
  useAdminUsers,
  useUpdateUser,
  useDeleteUser,
  useCreateUser,
  useInviteLink,
  useTemporaryPassword,
  type AdminUser,
} from "@/hooks/useAdmin";
import { useAuth } from "@/lib/auth";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { cn } from "@/lib/cn";
import { useCan } from "@/lib/roles";
import { useOrgCapacitySettings, useUpdateOrgCapacitySetting } from "@/hooks/useCapacityRebuild";
import { CAPACITY_BANDS, parseCapacityTiers, serializeCapacityTiers } from "@/lib/capacityScope";
import { useAddDepartment, useDeleteDepartment, useDepartments, useRenameDepartment, useSetDepartmentSelfManaged, type Department } from "@/hooks/useDepartments";
import { Building2, Pencil } from "lucide-react";

import { ACCESS_MATRIX, ROLES, ROLE_LABELS } from "@/lib/roles";

function apiError(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data
    ?.detail;
  return typeof detail === "string" ? detail : fallback;
}

export function Admin() {
  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <TopBar title="Admin" subtitle="Access requests, users & teams" />
      <div className="flex-1 overflow-y-auto bg-canvas">
        <div className="mx-auto max-w-6xl space-y-8 px-6 py-6">
          <AccessRequestsSection />
          <UsersSection />
          <TeamsSection />
          <DepartmentsSection />
          <CapacityScopeSection />
          <AccessMatrixSection />
        </div>
      </div>
    </div>
  );
}

function AccessRequestsSection() {
  const { data: requests, isLoading } = useAccessRequests("pending");
  const approve = useApproveRequest();
  const deny = useDenyRequest();

  return (
    <section>
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-ink-muted">
        Pending access requests
      </h2>
      <div className="rounded-xl border border-line bg-surface">
        {isLoading ? (
          <div className="flex items-center justify-center p-8 text-ink-subtle">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : !requests || requests.length === 0 ? (
          <p className="p-6 text-sm text-ink-subtle">No pending requests.</p>
        ) : (
          <ul className="divide-y divide-line-soft">
            {requests.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-4 p-4">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-ink">
                    {r.display_name}{" "}
                    <span className="font-normal text-ink-subtle">· {r.email}</span>
                  </p>
                  {r.reason && (
                    <p className="mt-0.5 truncate text-xs text-ink-muted">{r.reason}</p>
                  )}
                  <p className="mt-0.5 text-[11px] text-ink-subtle">
                    requested {r.created_at}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    disabled={approve.isPending}
                    onClick={() =>
                      approve.mutate(r.id, {
                        onSuccess: (u) => toast.success(`Approved ${u.email}`),
                        onError: (e) => toast.error(apiError(e, "Approve failed")),
                      })
                    }
                    className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    <Check className="h-3.5 w-3.5" /> Approve
                  </button>
                  <button
                    disabled={deny.isPending}
                    onClick={() =>
                      deny.mutate(r.id, {
                        onSuccess: () => toast.success(`Denied ${r.email}`),
                        onError: (e) => toast.error(apiError(e, "Deny failed")),
                      })
                    }
                    className="inline-flex items-center gap-1.5 rounded-lg border border-line-strong px-3 py-1.5 text-xs font-semibold text-ink-3 hover:bg-raised disabled:opacity-50"
                  >
                    <X className="h-3.5 w-3.5" /> Deny
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function UsersSection() {
  const { user: currentUser } = useAuth();
  const { data: users, isLoading } = useAdminUsers();
  const update = useUpdateUser();
  const del = useDeleteUser();
  const create = useCreateUser();
  const [busyId, setBusyId] = useState<number | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AdminUser | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [cEmail, setCEmail] = useState("");
  const [cName, setCName] = useState("");
  const [cRole, setCRole] = useState("user");
  const [cInvite, setCInvite] = useState(true);

  const openCreate = () => {
    setCEmail("");
    setCName("");
    setCRole("user");
    setCInvite(true);
    setShowCreate(true);
  };

  const submitCreate = (e: React.FormEvent) => {
    e.preventDefault();
    create.mutate(
      {
        email: cEmail.trim().toLowerCase(),
        display_name: cName.trim(),
        role: cRole,
        send_invite: cInvite,
      },
      {
        onSuccess: (res) => {
          toast.success(
            res.invite_sent
              ? `Created ${res.user.email} — invite emailed`
              : `Created ${res.user.email} (no email sent; they can use “Forgot password”)`,
          );
          setShowCreate(false);
        },
        onError: (e2) => toast.error(apiError(e2, "Could not create user")),
      },
    );
  };

  const run = (id: number, body: { role?: string; status?: string; can_view_agents?: boolean }, ok: string) => {
    setBusyId(id);
    update.mutate(
      { id, ...body },
      {
        onSuccess: () => toast.success(ok),
        onError: (e) => toast.error(apiError(e, "Update failed")),
        onSettled: () => setBusyId(null),
      },
    );
  };

  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">
          Users
        </h2>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
        >
          <UserPlus className="h-3.5 w-3.5" /> Add user
        </button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-line bg-surface">
        {isLoading ? (
          <div className="flex items-center justify-center p-8 text-ink-subtle">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line-soft text-left text-xs uppercase tracking-wide text-ink-subtle">
                <th className="px-4 py-3 font-medium">User</th>
                <th className="px-4 py-3 font-medium">Role</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium" title="Agents-console visibility — per-user grant, independent of role">Agents</th>
                <th className="px-4 py-3 font-medium">Last login</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-line-soft">
              {(users ?? []).map((u) => {
                const busy = busyId === u.id;
                const disabled = u.status === "disabled";
                return (
                  <tr key={u.id} className={cn(disabled && "bg-canvas/60")}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-ink">
                        {u.display_name || "—"}
                      </p>
                      <p className="text-xs text-ink-subtle">{u.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={u.role}
                        disabled={busy}
                        onChange={(e) =>
                          run(u.id, { role: e.target.value }, `Role → ${e.target.value}`)
                        }
                        className="rounded-lg border border-line-strong bg-surface px-2 py-1 text-xs font-medium text-ink-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        {ROLES.map((role) => (
                          <option key={role} value={role}>
                            {role}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
                          disabled
                            ? "bg-muted text-ink-3"
                            : "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
                        )}
                      >
                        <span
                          className={cn(
                            "h-1.5 w-1.5 rounded-full",
                            disabled ? "bg-slate-400" : "bg-emerald-500",
                          )}
                        />
                        {disabled ? "disabled" : "active"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        disabled={busy}
                        onClick={() =>
                          run(
                            u.id,
                            { can_view_agents: !u.can_view_agents },
                            u.can_view_agents ? "Agents console access revoked" : "Agents console access granted",
                          )
                        }
                        className={cn(
                          "rounded-full px-2.5 py-1 text-xs font-medium ring-1 disabled:opacity-50",
                          u.can_view_agents
                            ? "bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-300 ring-violet-200 dark:ring-violet-500/30 hover:bg-violet-100"
                            : "bg-canvas text-ink-subtle ring-line hover:bg-raised",
                        )}
                        title={u.can_view_agents
                          ? "Can see the Agents console — click to revoke"
                          : "No Agents console access — click to grant"}
                      >
                        {u.can_view_agents ? "granted" : "off"}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-xs text-ink-subtle">
                      {u.last_login_at ?? "never"}
                    </td>
                    <td className="whitespace-nowrap py-3 pl-2 pr-4">
                      {currentUser?.id !== u.id && (
                        <div className="flex items-center justify-end gap-1.5">
                          {!disabled && <GetThemIn user={u} />}
                          <button
                            disabled={busy}
                            onClick={() =>
                              run(
                                u.id,
                                { status: disabled ? "active" : "disabled" },
                                disabled ? "User activated" : "User deactivated",
                              )
                            }
                            className={cn(
                              "inline-flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border px-2.5 text-xs font-medium disabled:opacity-50",
                              disabled
                                ? "border-emerald-300 dark:border-emerald-500/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-500/15"
                                : "border-line-strong text-ink-3 hover:bg-raised",
                            )}
                          >
                            {busy ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : disabled ? (
                              <Power className="h-3.5 w-3.5" />
                            ) : (
                              <Ban className="h-3.5 w-3.5" />
                            )}
                            {disabled ? "Activate" : "Deactivate"}
                          </button>
                          <button
                            onClick={() => setPendingDelete(u)}
                            title={`Delete ${u.email}`}
                            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-ink-subtle transition-colors hover:bg-red-50 dark:hover:bg-red-500/15 hover:text-red-600 dark:hover:text-red-400"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <p className="mt-2 flex items-center gap-1.5 text-xs text-ink-subtle">
        <ShieldCheck className="h-3.5 w-3.5" />
        Disabling a user revokes their active sessions immediately.
      </p>

      <Dialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete user?</DialogTitle>
            <DialogDescription>
              This permanently deletes{" "}
              <span className="font-medium text-ink-2">
                {pendingDelete?.email}
              </span>{" "}
              and revokes their sessions. This can&rsquo;t be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <button className="rounded-lg border border-line-strong px-4 py-2 text-sm font-medium text-ink-3 hover:bg-raised">
                Cancel
              </button>
            </DialogClose>
            <button
              disabled={del.isPending}
              onClick={() => {
                const target = pendingDelete;
                if (!target) return;
                del.mutate(target.id, {
                  onSuccess: () => {
                    toast.success(`Deleted ${target.email}`);
                    setPendingDelete(null);
                  },
                  onError: (e) => toast.error(apiError(e, "Delete failed")),
                });
              }}
              className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
            >
              {del.isPending ? "Deleting…" : "Delete user"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCreate} onOpenChange={(open) => !open && setShowCreate(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add user</DialogTitle>
            <DialogDescription>
              Creates an account and emails a set-password invite. If email is off,
              they can set a password via &ldquo;Forgot password&rdquo;.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitCreate} className="space-y-3">
            <label className="block">
              <span className="text-sm font-medium text-ink-2">Email</span>
              <input
                type="email"
                value={cEmail}
                onChange={(e) => setCEmail(e.target.value)}
                required
                autoFocus
                className="mt-1.5 w-full rounded-lg border border-line-strong px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-ink-2">Full name</span>
              <input
                type="text"
                value={cName}
                onChange={(e) => setCName(e.target.value)}
                required
                maxLength={120}
                className="mt-1.5 w-full rounded-lg border border-line-strong px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-ink-2">Role</span>
              <select
                value={cRole}
                onChange={(e) => setCRole(e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-line-strong bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm text-ink-2">
              <input
                type="checkbox"
                checked={cInvite}
                onChange={(e) => setCInvite(e.target.checked)}
                className="rounded border-line-strong"
              />
              Email a set-password invite
            </label>
            <DialogFooter>
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="rounded-lg border border-line-strong px-4 py-2 text-sm font-medium text-ink-3 hover:bg-raised"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={create.isPending || !cEmail.trim() || !cName.trim()}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {create.isPending ? "Creating…" : "Create user"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}


/** Get-them-in-now (Brett, 2026-10-12, after Brandon and Sam couldn't
 *  finish the emailed invite): hand an admin a sign-in link to paste
 *  anywhere, or a one-time temporary password to pass on directly. */
function GetThemIn({ user }: { user: AdminUser }) {
  const link = useInviteLink();
  const temp = useTemporaryPassword();
  const [reveal, setReveal] = useState<{ kind: "link" | "password"; value: string; note: string } | null>(null);

  const copy = async (text: string) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); } catch { toast.error("Couldn't copy — select it and copy by hand."); }
  };

  return (
    <>
      <button
        disabled={link.isPending}
        onClick={() => link.mutate(user.id, {
          onSuccess: async (d) => { setReveal({ kind: "link", value: d.link, note: `Sign-in link for ${user.email}. Copied — nothing was emailed; send it yourself. Works for 7 days.` }); await copy(d.link); },
          onError: (e) => toast.error(apiError(e, "Couldn't make a link")),
        })}
        className="inline-flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-line-strong px-2.5 text-xs font-medium text-ink-3 hover:bg-raised disabled:opacity-50"
        title="Copy a fresh sign-in link to send yourself — the planner does not email it"
        data-testid={`invite-link-${user.id}`}
      >
        {link.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />} Sign-in link
      </button>
      <button
        disabled={temp.isPending}
        onClick={() => temp.mutate(user.id, {
          onSuccess: (d) => setReveal({ kind: "password", value: d.password, note: `Temporary password for ${d.email}. Shown once — pass it on directly. They can change it on their Profile.` }),
          onError: (e) => toast.error(apiError(e, "Couldn't set a password")),
        })}
        className="inline-flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-line-strong px-2.5 text-xs font-medium text-ink-3 hover:bg-raised disabled:opacity-50"
        title="Set a temporary password you can give them right now"
        data-testid={`temp-password-${user.id}`}
      >
        {temp.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <KeyRound className="h-3.5 w-3.5" />} Temp password
      </button>
      <Dialog open={!!reveal} onOpenChange={(o) => !o && setReveal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{reveal?.kind === "link" ? "Sign-in link" : "Temporary password"}</DialogTitle>
            <DialogDescription>{reveal?.note}</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <code className="flex-1 select-all break-all rounded-md bg-raised px-3 py-2 font-mono text-sm text-ink" data-testid="get-them-in-value">{reveal?.value}</code>
            <button onClick={() => reveal && copy(reveal.value)} className="inline-flex items-center gap-1 rounded-lg border border-line px-2.5 py-1.5 text-xs font-medium hover:bg-raised" title="Copy">
              <Copy className="h-3.5 w-3.5" /> Copy
            </button>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <button className="rounded-lg bg-brand-navy-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-navy-700">Done</button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Departments (Brett, 2026-10-22: "IT Infrastructure wants to join and I
 *  can't add a department"). A department can exist before its first
 *  project; rename re-tags every project and enhancement;
 *  remove only when nothing uses it. PMO and Admin. */
function DepartmentsSection() {
  const { isPmo } = useCan();
  const { data: rows = [], isLoading } = useDepartments();
  const add = useAddDepartment();
  const rename = useRenameDepartment();
  const remove = useDeleteDepartment();
  const selfManaged = useSetDepartmentSelfManaged();
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState<{ name: string; value: string } | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const err = (e: unknown, fb: string) => toast.error(apiError(e, fb));

  const submitAdd = () => {
    const v = draft.trim();
    if (!v) return;
    add.mutate(v, { onSuccess: () => { toast.success(`${v} added`); setDraft(""); }, onError: (e) => err(e, "Couldn't add the department") });
  };
  const submitRename = () => {
    if (!editing) return;
    const to = editing.value.trim();
    if (!to || to === editing.name) { setEditing(null); return; }
    rename.mutate({ from: editing.name, to }, {
      onSuccess: () => { toast.success(`${editing.name} renamed to ${to} — projects and enhancements follow`); setEditing(null); },
      onError: (e) => err(e, "Couldn't rename"),
    });
  };
  const inUse = (d: Department) => d.projects + d.initiatives + d.enhancements;

  return (
    <section data-testid="departments-admin" id="departments">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">Departments</h2>
        {isPmo && (
          <div className="flex items-center gap-2">
            <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submitAdd()} placeholder="New department" aria-label="New department name"
              className="w-56 rounded-md border border-line bg-surface px-2.5 py-1.5 text-sm text-ink focus:border-brand-navy-400 focus:outline-none" data-testid="dept-new-name" />
            <button onClick={submitAdd} disabled={add.isPending || !draft.trim()} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-navy-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-navy-700 disabled:opacity-50" data-testid="dept-add">
              <Building2 className="h-3.5 w-3.5" /> Add department
            </button>
          </div>
        )}
      </div>
      <div className="overflow-hidden rounded-xl border border-line bg-surface">
        {isLoading ? (
          <div className="flex items-center justify-center p-8 text-ink-subtle"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-ink-muted">No departments yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line-soft text-left text-xs uppercase tracking-wide text-ink-subtle">
                <th className="px-4 py-3 font-medium">Department</th>
                <th className="px-4 py-3 text-right font-medium">Projects</th>
                <th className="px-4 py-3 text-right font-medium">Enhancements</th>
                <th className="px-4 py-3 font-medium" title="A User's new project here is saved Approved and stays off the board — no request, no PMO gate">No approval needed</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.name} className="border-b border-line-soft last:border-0" data-testid="dept-row" data-name={d.name}>
                  <td className="px-4 py-2.5 text-ink">
                    {editing?.name === d.name ? (
                      <span className="flex items-center gap-2">
                        <input autoFocus value={editing.value} onChange={(e) => setEditing({ name: d.name, value: e.target.value })}
                          onKeyDown={(e) => { if (e.key === "Enter") submitRename(); if (e.key === "Escape") setEditing(null); }}
                          aria-label={`New name for ${d.name}`} className="w-64 rounded-md border border-line bg-surface px-2 py-1 text-sm text-ink focus:border-brand-navy-400 focus:outline-none" />
                        <button onClick={submitRename} disabled={rename.isPending} className="rounded-md bg-brand-navy-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-brand-navy-700 disabled:opacity-50">Save</button>
                        <button onClick={() => setEditing(null)} className="text-xs text-ink-subtle hover:underline">Cancel</button>
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <span className="font-medium">{d.name}</span>
                        {isPmo && (
                          <button onClick={() => setEditing({ name: d.name, value: d.name })} className="rounded p-1 text-ink-subtle hover:bg-raised hover:text-ink-3" title={`Rename ${d.name}`} aria-label={`Rename ${d.name}`}>
                            <Pencil className="h-3 w-3" />
                          </button>
                        )}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-ink-2">{d.projects}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-ink-2">{d.enhancements}</td>
                  <td className="px-4 py-2.5">
                    <label className="inline-flex items-center gap-2 text-xs text-ink-2">
                      <input type="checkbox" checked={d.self_managed} disabled={!isPmo || selfManaged.isPending}
                        onChange={(e) => selfManaged.mutate({ name: d.name, self_managed: e.target.checked }, {
                          onSuccess: () => toast.success(e.target.checked ? `${d.name} projects no longer need PMO approval` : `${d.name} projects go through the PMO again`),
                          onError: (er) => err(er, "Couldn't update"),
                        })}
                        className="h-4 w-4 rounded border-line" data-testid={`dept-self-managed-${d.name}`} />
                      {d.self_managed ? "Self-managed" : "Via PMO"}
                    </label>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {isPmo && (inUse(d) === 0 ? (
                      confirming === d.name ? (
                        <span className="flex items-center justify-end gap-1.5">
                          <button onClick={() => remove.mutate(d.name, { onSuccess: () => { toast.success(`${d.name} removed`); setConfirming(null); }, onError: (e) => err(e, "Couldn't remove") })}
                            className="rounded-md bg-rose-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-rose-700" data-testid="dept-remove-confirm">Remove?</button>
                          <button onClick={() => setConfirming(null)} className="text-xs text-ink-subtle hover:underline">Cancel</button>
                        </span>
                      ) : (
                        <button onClick={() => setConfirming(d.name)} className="rounded-lg p-1.5 text-ink-subtle hover:bg-red-50 dark:hover:bg-red-500/15 hover:text-red-600 dark:hover:text-red-400" title={`Remove ${d.name}`} aria-label={`Remove ${d.name}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )
                    ) : (
                      <span className="text-[11px] text-ink-subtle" title="Move its projects and enhancements first">in use</span>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="border-t border-line-soft px-4 py-2 text-[11px] text-ink-muted">
          A department shows on the Departments page as soon as it is added. Renaming re-tags every project and enhancement that carries the old name.
        </p>
      </div>
    </section>
  );
}

/** Which board bands count toward capacity (Brett, 2026-10-06): "today
 *  just the Top 10, maybe tomorrow Top 10 + Just Do". One setting read by
 *  /capacity, the outlook, the team pages and project Team cards. */
function CapacityScopeSection() {
  const { isPmo } = useCan();
  const { data: settings, isLoading } = useOrgCapacitySettings();
  const update = useUpdateOrgCapacitySetting();
  const raw = settings?.find((s) => s.key === "capacity_tiers")?.value;
  const tiers = parseCapacityTiers(raw);

  const toggle = (key: string) => {
    const next = new Set(tiers);
    if (next.has(key)) next.delete(key); else next.add(key);
    if (next.size === 0) { toast.error("At least one band must count toward capacity."); return; }
    update.mutate({ key: "capacity_tiers", value: serializeCapacityTiers(next) }, {
      onSuccess: () => toast.success("Capacity scope saved — /capacity, the outlook and Team cards now count those bands."),
      onError: (e) => toast.error(apiError(e, "Couldn't save the capacity scope")),
    });
  };

  return (
    <section data-testid="capacity-scope">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-ink-muted">
        Capacity scope
      </h2>
      <div className="rounded-xl border border-line bg-surface p-5">
        <p className="text-sm text-ink-2">
          Which board bands count toward capacity. A person's assigned hours on any other project are kept but do not reduce their availability on /capacity, the 13-week outlook, team pages, or a project's Team card.
        </p>
        <div className="mt-4 flex flex-wrap gap-6">
          {CAPACITY_BANDS.map((b) => (
            <label key={b.key} className={cn("flex items-start gap-2.5 text-sm", !isPmo && "opacity-70")}>
              <input
                type="checkbox"
                checked={tiers.has(b.key)}
                disabled={!isPmo || isLoading || update.isPending}
                onChange={() => toggle(b.key)}
                className="mt-0.5 h-4 w-4 rounded border-line"
                data-testid={`capacity-scope-${b.key}`}
              />
              <span>
                <span className="font-medium text-ink">{b.label}</span>
                <span className="block text-xs text-ink-muted">{b.hint}</span>
              </span>
            </label>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-ink-muted">
          {isPmo ? "Takes effect immediately for everyone." : "PMO and Admin can change this."}
        </p>
      </div>
    </section>
  );
}

/** What each role unlocks (2026-09-28, Brett: "so we know what users have
 *  access to" — the reference for choosing a user's type). Source of
 *  truth for the copy is lib/roles.ts; the server enforces it. */
function AccessMatrixSection() {
  return (
    <section data-testid="access-matrix">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-ink-muted">
        Access levels
      </h2>
      <div className="overflow-hidden rounded-xl border border-line bg-surface">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line-soft text-left text-xs uppercase tracking-wide text-ink-subtle">
              <th className="px-4 py-3 font-medium">Can…</th>
              {ROLES.map((r) => (
                <th key={r} className="px-4 py-3 text-center font-medium">{ROLE_LABELS[r]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ACCESS_MATRIX.map((row) => (
              <tr key={row.capability} className="border-b border-line-soft last:border-0">
                <td className="px-4 py-2.5 text-ink-2">{row.capability}</td>
                {ROLES.map((r) => {
                  const ok = ROLES.indexOf(r) >= ROLES.indexOf(row.min);
                  return (
                    <td key={r} className={cn("px-4 py-2.5 text-center", ok ? "text-emerald-600 dark:text-emerald-400" : "text-ink-faint")} aria-label={ok ? `${ROLE_LABELS[r]}: yes` : `${ROLE_LABELS[r]}: no`}>
                      {ok ? "✓" : "—"}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="border-t border-line-soft px-4 py-2 text-[11px] text-ink-muted">
          Viewer = read-only. User = works on projects. PMO = decides and staffs. Admin = configures the planner and manages accounts.
        </p>
      </div>
    </section>
  );
}
